/* =============================================================================
   Cart participants – with localStorage fallback (14‑day TTL)
   ============================================================================= */

/* -----------------------------------------------------------------------------
   light‑weight localStorage helper – safely handles unavailable storage
   now keyed by DELEGATE = `${lineIndex}-${delegateIndex}`
----------------------------------------------------------------------------- */
const STORAGE_KEY = 'cartParticipantsCache';
const CACHE_TTL   = 14 * 24 * 60 * 60 * 1000; // 14 days → ms

function _canStore () {
  try {
    const test = '__ls_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (_) {
    return false;
  }
}

function loadCache () {
  if (!_canStore()) return {};
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch (_) {
    return {};
  }
}

function saveCache (obj) {
  if (!_canStore()) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
}

function _delegateKey (line, delegate) {
  return `${line}_${delegate}`;
}

function cleanupCache () {
  const o = loadCache();
  const now = Date.now();
  let dirty = false;
  for (const [k, v] of Object.entries(o)) {
    if (!v.timestamp || now - v.timestamp > CACHE_TTL) {
      delete o[k];
      dirty = true;
    }
  }
  if (dirty) saveCache(o);
}

function getDelegateCache (line, delegate) {
  cleanupCache();
  const o = loadCache();
  return o[_delegateKey(line, delegate)]?.values || null;
}

function setFieldCache (line, delegate, name, value) {
  if (!_canStore()) return;
  const o   = loadCache();
  const key = _delegateKey(line, delegate);
  o[key] = o[key] || { timestamp: Date.now(), values: {} };
  o[key].values[name] = value;
  o[key].timestamp    = Date.now();
  saveCache(o);
}

function removeDelegateCache (line, delegate) {
  if (!_canStore()) return;
  const o   = loadCache();
  const key = _delegateKey(line, delegate);
  if (o[key]) {
    delete o[key];
    saveCache(o);
  }
}

function clearAllCache () {
  if (_canStore()) localStorage.removeItem(STORAGE_KEY);
}

cleanupCache();

/* =========================
   cart-participant-row
   ========================= */
if (!customElements.get('cart-participant-row')) {
  customElements.define('cart-participant-row', class CartParticipantRow extends HTMLElement {
    constructor () {
      super();
      this.processing = false;

      /* ----- quick DOM look‑ups ----- */
      this.selectors = {
        properties : this?.querySelector('[name="properties"]')?.textContent,
        remove     : this?.querySelector('[data-button="remove"]'),
        add        : this?.querySelector('[data-button="add"]'),
        fields     : this?.querySelectorAll('input, select, textarea'),
        fileRemove : this?.querySelector('.file-remove'),
        available  : this?.querySelector('[available-for-sale="false"]'),
        cartItem   : this?.closest('.cart-item')
      };

      /* ----- immutable data coming from Liquid ----- */
      this.data = {
        handle    : this.dataset.productHandle,
        variant   : this.dataset.variantId,
        key       : this.dataset.rowKey,         // legacy
        quantity  : parseInt(this.dataset.quantity, 10),
        delegate  : this.dataset.delegateIndex,
        rowIndex  : parseInt(this.dataset.rowIndex, 10),
        line      : parseInt(this.dataset.lineIndex, 10),   // line index within cart
        properties: JSON.parse(this.selectors.properties || '[]').filter(([k]) => k !== 'properties')
      };

      /* preload any cached values */
      this.prefillFromCache();

      /* instant removal if no longer available */
      if (this.selectors.available && this.selectors.available.getAttribute('available-for-sale') === 'false') {
        fetch(`${routes.cart_change_url}`, {
          method : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({ quantity: 0, id: this.data.variant, sections: 'default-cart' })
        }).then(() => window.location.reload());
        this.selectors.cartItem?.remove();
      }
    }

    /* ================= lifecycle ================= */
    connectedCallback () {
      const debounce = (fn, d) => {
        let t, ctx, args;
        const later = () => { t = null; fn.apply(ctx, args); };
        return function () { ctx = this; args = arguments; clearTimeout(t); t = setTimeout(later, d); };
      };

      this.selectors.remove?.addEventListener('click', this.delete.bind(this));
      console.log('cart-participant-row', this.data);
      this.selectors.add?.addEventListener('click', this.add.bind(this));

      this.selectors.fields?.forEach(f => {
        f.addEventListener('input',  debounce(this.update.bind(this), 250));
        f.addEventListener('change', this.update.bind(this));
      });

      this.selectors.fileRemove?.addEventListener('click', e => {
        e.preventDefault();
        const fileInput = this.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
          this.update({ target: fileInput });
        }
      });
    }

    /* expose flush so parent can sync all edits before submit */
    flush () {
      const tasks = Array.from(this.selectors.fields || [])
      .map(f => this.update({ target: f }))
      .filter(Boolean);                  // undefined for <input type=file> w/out value
      return Promise.all(tasks);
    }

    /* preload cached values (skips <input type="file">) */
    prefillFromCache () {
      const cache = getDelegateCache(this.data.line, this.data.delegate);
      if (!cache) return;

      this.selectors.fields?.forEach(f => {
        if (f.type === 'file') return;
        if (cache.hasOwnProperty(f.name)) {
          if (f.type === 'checkbox') f.checked = cache[f.name];
          else f.value = cache[f.name];
        }
      });
    }

    /* ================= validation ================= */
    validateInput (name, value) {
      switch (name) {
        case '_phone':
          //return /^((\+44\s?(\(0\)\s?)?)|0)(?:\d\s?){9,10}$/.test(value);
          // Accept UK and international numbers, allow +, spaces, dashes, parentheses, min 8 digits
          return /^(\+?\d[\d\s\-().]{7,})$/.test(value);
        case '_dob': {
          const fullDate = /^\d{4}-\d{2}-\d{2}$/;
          if (!fullDate.test(value)) return true; // still typing
          const dob = new Date(value);
          if (isNaN(dob.getTime())) return false;
          const today   = new Date();
          let age       = today.getFullYear() - dob.getFullYear();
          const mDiff   = today.getMonth() - dob.getMonth();
          if (mDiff < 0 || (mDiff === 0 && today.getDate() < dob.getDate())) age--;
          if (age < 16) {
            const dlg = document.getElementById('modal-errors');
            const bd  = dlg?.querySelector('.modal-body');
            if (bd) bd.textContent = window?.errorMessages?.cart_age;
            dlg?.classList.add('is-open');
            return false;
          }
          if (age > 99) return false;
          return true;
        }
        default:
          return true;
      }
    }

    /* ================= local update ================= */
    update (evt) {
      const target = evt.target;
      const { name } = target;
      const value = target.type === 'file' && target.files[0]
        ? target.files[0].name
        : (target.type === 'checkbox' ? target.checked : target.value);

      if (!this.validateInput(name, value)) {
        target.classList.add('invalid');
        return;
      }
      target.classList.remove('invalid');

      /* write to component + parent */
      if (target.type === 'file' && target.files[0]) {
        return new Promise(res => {
          const reader = new FileReader();
          reader.onload = () => {
            this.dispatchEvent(new CustomEvent('onInputSaved', { bubbles: true, detail: { name, value: reader.result } }));
            setFieldCache(this.data.line, this.data.delegate, name, reader.result);
            res();
          };
          reader.readAsDataURL(target.files[0]);
        });
      } else {
        this.dispatchEvent(new CustomEvent('onInputSaved', { bubbles: true, detail: { name, value } }));
        setFieldCache(this.data.line, this.data.delegate, name, value);
        return Promise.resolve();
      }
    }

    /* ================= row actions ================= */
    delete () {
      if (this.processing) return; this.processing = true;
      const { line, delegate } = this.data;
      this.dispatchEvent(new CustomEvent('onParticipantRemoved', { bubbles: true, detail: { line, delegate } }));
      removeDelegateCache(line, delegate);
      const container = this.closest('.cart-item__details');
      if (container?.children.length === 1) this.closest('.cart-item')?.remove();
      else this.remove();
      this.processing = false;
    }

    add () {
      if (this.processing) return; this.processing = true;
      const { line, delegate } = this.data;
      this.dispatchEvent(new CustomEvent('onParticipantAdded', { bubbles: true, detail: { line, delegate } }));
      this.processing = false;
      console.log('Participant added:', { line, delegate });
    }

    /* util */
    propertiesToObject (arr = []) {
      return arr.reduce((o, [k, v]) => {
        if (k !== 'properties') o[k] = v;  // skip accidental nested key
        return o;
      }, {});
    }
  });
}

/* =========================
   cart-participants
   ========================= */
if (!customElements.get('cart-participants')) {
  customElements.define('cart-participants', class CartParticipants extends HTMLElement {
    constructor () {
      super();
      this.processing = false;
      this.rows       = Array.from(this.querySelectorAll('cart-participant-row'));
      this.bookingCheckbox = this.querySelector('#booking');
      this.resultsCheckbox = this.querySelector('#results');
      this.form            = this.closest('form#cart');

      /* snapshot keyed by line index */
      this.cart = Object.values(this.rows.reduce((acc, row) => {
        const d = row.data;
        if (!acc[d.line]) acc[d.line] = { line: d.line, variant: d.variant, participants: [] };
        const props = JSON.parse(JSON.stringify(d.properties || []));
        console.log('Row properties:', props);
        if (!props.some(([k]) => k === '_delegate_index')) {
          props.push(['_delegate_index', d.delegate]);
        }
        acc[d.line].participants.push({
          id: d.variant,
          properties: props,
          delegate: d.delegate,
        });
        return acc;
      }, {}));

      this.cartAttributes = { booking: this.bookingCheckbox?.checked || false, results: this.resultsCheckbox?.checked || false };

      /* child events */
      this.addEventListener('onInputSaved',        this.onInputSaved.bind(this));
      this.addEventListener('onParticipantRemoved', this.onParticipantRemoved.bind(this));
      this.addEventListener('onParticipantAdded',   this.onParticipantAdded.bind(this));

      //physical item removal
      this.querySelectorAll('.physical_item').forEach(item => {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          // Find the closest cart-item
          const cartItem = item.closest('.cart-item');
          if (!cartItem) return;

          // Find the .cart-item__remove-button inside cartItem and get its variant-id attribute
          const removeButton = cartItem.querySelector('.cart-item__remove-button');
          const variantId = removeButton ? removeButton.getAttribute('variant-id') : null;
          if(!variantId) return;
          // Send a request to remove the item from the cart
            fetch(`${routes.cart_change_url}`, {
                method : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body   : JSON.stringify({ quantity: 0, id: variantId, sections: 'default-cart' })
              }).then(() => window.location.reload());
              this.selectors.cartItem?.remove();
        });
      });

      // quantity inputs for physical items
      this.querySelectorAll('quantity-input').forEach(qtyInput => {
        const input = qtyInput.querySelector('.quantity__input');
        const minusBtn = qtyInput.querySelector('button[name="minus"]');
        const plusBtn = qtyInput.querySelector('button[name="plus"]');
        console.debug('quantity-input', qtyInput, input, minusBtn, plusBtn);
        if (!input) return;

        // Helper to update quantity in Shopify cart and refresh UI
        const updateCartQuantity = (newQty) => {
          // Find the closest cart-item-row or cart-item to get the variant id
          const cartItemRow = qtyInput.closest('cart-item-row');
          if (!cartItemRow) return;
          const removeButton = cartItemRow.querySelector('.cart-item__remove-button');
          const variantId = removeButton ? removeButton.getAttribute('variant-id') : cartItemRow.getAttribute('variant-id');
          if (!variantId) return;

          fetch(`${routes.cart_change_url}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity: newQty, id: variantId, sections: 'default-cart' })
          }).then(() => window.location.reload());
        };
          minusBtn?.addEventListener('click', () => {
            let current = parseInt(input.value, 10) || 0;
            console.debug('minusBtn clicked, current quantity:', current);
              if (current >= 1) {
              updateCartQuantity(current);
              }else if (current < 1) {
                const cartItemRow = qtyInput.closest('cart-item-row');
                if (!cartItemRow) return;
                const removeButton = cartItemRow.querySelector('.cart-item__remove-button');
                const variantId = removeButton ? removeButton.getAttribute('variant-id') : cartItemRow.getAttribute('variant-id');
                if (!variantId) return;

                fetch(`${routes.cart_change_url}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ quantity: 0, id: variantId, sections: 'default-cart' })
                }).then(() => window.location.reload());
              }
          });
          plusBtn?.addEventListener('click', () => {
            let current = parseInt(input.value, 10) || 1;
            updateCartQuantity(current);
          });
      });

      /* attribute checkboxes */
      this.bookingCheckbox?.addEventListener('change', () => { this.cartAttributes.booking = this.bookingCheckbox.checked; });
      this.resultsCheckbox?.addEventListener('change', () => { this.cartAttributes.results = this.resultsCheckbox.checked; });

      /* form submit */
      this.form?.addEventListener('submit', async e => {
        e.preventDefault();

        await Promise.all(this.rows.map(r => r.flush()));
        const invalid = this.querySelectorAll('.invalid');
        if (invalid.length) { invalid[0].focus(); return; }

        // Validate unique names before proceeding
        if (!this.validateUniqueNames()) return;

        this.buildCartSnapshot();

        await this.saveChanges('redirect');
      });
      
    }

     validateUniqueNames() {
    // Map: line index (cart-item) -> Set of name keys
    const lineNameMap = {};
    let hasDuplicate = false;
    let duplicateName = '';
    let duplicateLine = null;

    this.rows.forEach(row => {
      const firstNameInput = row.querySelector('[name="_first_name"]');
      const lastNameInput = row.querySelector('[name="_last_name"]');
      const firstName = firstNameInput ? firstNameInput.value.trim() : '';
      const lastName = lastNameInput ? lastNameInput.value.trim() : '';
      if (!firstName || !lastName) return; // skip empty

      const key = `${firstName.toLowerCase()}|${lastName.toLowerCase()}`;
      const line = row.data.line;

      if (!lineNameMap[line]) lineNameMap[line] = new Set();
      if (lineNameMap[line].has(key)) {
        hasDuplicate = true;
        duplicateName = `${firstName} ${lastName}`;
        duplicateLine = line;
      }
      lineNameMap[line].add(key);
    });

console.debug('Unique name validation:', lineNameMap, hasDuplicate, duplicateName);

      if (hasDuplicate) {
        const dlg = document.getElementById('modal-errors');
        const body = dlg?.querySelector('.modal-body');
        if (body) body.textContent = `You have entered this delegate twice: ${duplicateName}.\nPlease update with the correct details for each delegate.`;
        dlg?.classList.add('is-open');
        return false;
      }
      return true;
    }


    buildCartSnapshot () {

        this.cart = Array.from(this.querySelectorAll('cart-participant-row')).map(row => {
        const d = row.data;
        return {
          line: d.line,
          variant: d.variant,
          key: row.dataset.lineKey, // key
          participants: [{
            id: d.variant,
            delegate: d.delegate,
            properties: Object.entries(getDelegateCache(d.line, d.delegate) || {})
              .map(([k, v]) => [k, v])
              .concat([['_delegate_index', d.delegate]])
          }]
        };
      });
        // --- Add physical items that are not participants ---
      const physicalItems = Array.from(this.querySelectorAll('cart-item-row[data-physical="true"]'));
      physicalItems.forEach(cartItem => {
        // Get variantId from .cart-item__remove-button or data attribute
        const removeButton = cartItem.querySelector('.cart-item__remove-button');
        const variantId = removeButton ? removeButton.getAttribute('variant-id') : cartItem.getAttribute('variant-id');
        const qtyEl = cartItem.querySelector('.quantity__input');
        console.debug('Processing physical item:', qtyEl);
        let quantity = 1;
            if (qtyEl) {
              quantity = parseInt(qtyEl.value, 10) || 1;
        }
        // Check if already in cart snapshot (by variant)
        const alreadyInCart = this.cart.some(item => item.variant == variantId);
        if (variantId && !alreadyInCart) {
          this.cart.push({
            line: null,
            variant: variantId,
            quantity: quantity,
            participants: []
            
          });
          console.debug('Added non-participant physical item to cart:', variantId);
        }
      });
    }

    propertiesToObject (arr = []) {
      return arr.reduce((o, [k, v]) => {
        if (k !== 'properties') o[k] = v;  // skip accidental nested key
        return o;
      }, {});
    }

    /* ================= child events ================= */
    onInputSaved (e) {
      const { name, value }   = e.detail;
      const { line, delegate } = e.target.data;
      const item = this.cart.find(c => c.line === line); if (!item) return;
      const p    = item.participants[delegate]; if (!p) return;
      if (!p.properties.some(([k]) => k === '_delegate_index')) p.properties.push(['_delegate_index', p.delegate]);

      const idx = p.properties.findIndex(([k]) => k === name);
      idx > -1 ? p.properties[idx][1] = value : p.properties.push([name, value]);
    }

    /*async onParticipantRemoved (e) {
      const { line, delegate } = e.detail;
      const item = this.cart.find(c => c.line === line); if (!item) return;
      item.participants = item.participants.filter(p => p.delegate !== delegate);
      if (!item.participants.length) this.cart = this.cart.filter(c => c.line !== line);
      await this.saveChanges('reload');
    }*/
   /*async onParticipantRemoved(e) {
      const { line, delegate } = e.detail;
      const item = this.cart.find(c => c.line === line); 
      if (!item) return;

      item.participants = item.participants.filter(p => p.delegate !== delegate);
      console.log("remove", item.participants)

        // Find the cart-participant-row for this line and delegate
        const row = this.rows.find(r => r.data.line == line && r.data.delegate == delegate);
        const lineKey = row?.dataset.rowKey;
        console.log("lineKey", lineKey, row)

        // Fallback: Try to find the line key from window.cart.items if not found
        let finalLineKey = lineKey;
        if (!finalLineKey && window.cart && window.cart.items) {
          const delegateProps = getDelegateCache(line, delegate) || {};
          finalLineKey = window.cart.items.find(cartItem =>
            cartItem.variant_id == item.variant &&
            cartItem.properties &&
            Object.entries(delegateProps).every(([k, v]) => cartItem.properties[k] == v)
          )?.key;
        }
        console.log("finalLineKey", finalLineKey)
      //if (!item.participants.length) {
        // Remove the item from the cart in Shopify
        console.log("remove item", item)
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: finalLineKey, quantity: 0 })
        });
        // Remove from local cart snapshot
        this.cart = this.cart.filter(c => c.line !== line);
        window.location.reload(); // Optionally refresh the cart UI
        return;
      
      //window.location.reload();
      //await this.saveChanges('reload');
    //}
    }*/

    async onParticipantRemoved(e) {
      const { line, delegate } = e.detail;
      const item = this.cart.find(c => c.line === line); 
      if (!item) return;

      item.participants = item.participants.filter(p => p.delegate !== delegate);
      console.log("remove", item.participants);

      // Find the cart-participant-row for this line and delegate
      const row = this.rows.find(r => r.data.line == line && r.data.delegate == delegate);
      const lineKey = row?.dataset.rowKey;

      // Fallback: Try to find the line key from window.cart.items if not found
      let finalLineKey = lineKey;
      if (!finalLineKey && window.cart && window.cart.items) {
        const delegateProps = getDelegateCache(line, delegate) || {};
        finalLineKey = window.cart.items.find(cartItem =>
          cartItem.variant_id == item.variant &&
          cartItem.properties &&
          Object.entries(delegateProps).every(([k, v]) => cartItem.properties[k] == v)
        )?.key;
      }

      // Remove the delegate line item from Shopify cart
      if (finalLineKey) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: finalLineKey, quantity: 0 })
        });
        console.log('Removed delegate line item:', finalLineKey);
      }

      // After removing delegate, check for Rebuy products to remove
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Get all remaining variant IDs in the cart (excluding Rebuy products)
      const remainingVariants = cart.items
        .filter(cartItem => !cartItem.properties || cartItem.properties._source !== 'Rebuy')
        .map(cartItem => String(cartItem.variant_id));

      console.log('Remaining variants in cart:', remainingVariants);

      // Find Rebuy products whose linked variants are no longer in cart
      const rebuyItemsToRemove = cart.items.filter(cartItem => {
        if (!cartItem.properties || cartItem.properties._source !== 'Rebuy') return false;
        
        const linkedVariants = (cartItem.properties._linked_to_variants || '')
          .split(',')
          .map(v => v.trim())
          .filter(v => v);

        console.log('Rebuy item linked to variants:', linkedVariants);

        // Remove if NONE of the linked variants exist in cart anymore
        const hasAnyLinkedVariant = linkedVariants.some(v => remainingVariants.includes(v));
        return !hasAnyLinkedVariant;
      });

      console.log('Rebuy items to remove:', rebuyItemsToRemove);

      // Remove each orphaned Rebuy product
      for (const rebuyItem of rebuyItemsToRemove) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: rebuyItem.key, quantity: 0 })
        });
        console.log('Removed orphaned Rebuy item:', rebuyItem.key);
      }

      // Remove from local cart snapshot
      this.cart = this.cart.filter(c => c.line !== line);
      
      // Reload to update UI
      window.location.reload();
    }

    async onParticipantAdded (e) {
      const { line } = e.detail;
      const item = this.cart.find(c => c.line === line); if (!item) return;
      const newDel = item.participants.length;

      const clone = {
        id: item.variant,
        delegate: newDel,
        properties: [['_delegate_index', newDel]]
      };
      //item.participants.push(clone);
      //await this.saveChanges('reload');
      //item.participants.push(clone);
      //console.log('Participant added:', item, clone);
      // Add the new delegate as a new line item in Shopify cart
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: item.variant,
            quantity: 1,
            properties: { _delegate_index: newDel }
          }]
        })
      });
      // Reload to show the new delegate row
      //window.location.reload();
      await this.saveChanges('reload');
    }

    /* ================= persistence ================= */
    async saveChanges (action) {
      this.process(true);
      /*try {
        await fetch('/cart/clear.js', { method: 'POST' });
      } catch (err) {
        console.error('clear cart err', err);
        this.process(false);
        return;
      }*/

      if (!this.rows.length) {
        // Only process physical items and attributes
        console.log('No participants, processing physical items only');
        const physicalItems = Array.from(this.querySelectorAll('cart-item-row[data-physical="true"]'));
        for (const cartItem of physicalItems) {
          const removeButton = cartItem.querySelector('.cart-item__remove-button');
          const variantId = removeButton ? removeButton.getAttribute('variant-id') : cartItem.getAttribute('variant-id');
          const qtyEl = cartItem.querySelector('.quantity__input');
          let quantity = 1;
          if (qtyEl) {
            quantity = parseInt(qtyEl.value, 10) || 1;
          }
          const lineKey = cartItem.dataset.lineKey;
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: lineKey || variantId,
              quantity: quantity
            })
          });
        }
        // Save attributes and proceed
        await this.saveAttributes(action);
        this.process(false);
        clearAllCache();
        return;
      }

      const gKey = obj => JSON.stringify(Object.keys(obj).sort().reduce((a, k) => (a[k] = obj[k], a), {}));
      const groups = {};
      for (const item of this.cart) {
        if (item.participants.length) {
        for (const p of item.participants) {
          const props = this.propertiesToObject(p.properties);
          const key   = `${item.variant}_${gKey(props)}`;
          groups[key] ? groups[key].quantity += 1 : groups[key] = { id: item.variant, quantity: 1, properties: props };
        }}
      }
      const items = Object.values(groups);
      if (!items.length) {
        this.process(false);
        window.location.reload();
        return;
      }
      
      console.log('items', items);

            // --- Update delegate lines ---

    for (const row of this.rows) {
      const d = row.data;
      const allowedKeys = [
        '_first_name',
        '_last_name',
        '_email',
        '_phone',
        '_gender',
        '_dob',
        '_job_title',
        '_dietary_requirements',
        '_additional_comments',
        '_delegate_index',
        '_uuid'
      ];
      
      // Get properties from cache first
      let rawProps = getDelegateCache(d.line, d.delegate) || {};
      
      // Fallback: If cache is empty or incomplete, get values directly from DOM inputs
      if (Object.keys(rawProps).length === 0) {
        console.warn('Cache empty for delegate, reading from DOM', { line: d.line, delegate: d.delegate });
        rawProps = {};
        const inputs = row.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
          if (input.name && allowedKeys.includes(input.name)) {
            if (input.type === 'checkbox') {
              rawProps[input.name] = input.checked;
            } else if (input.type === 'file' && input.files[0]) {
              // Skip file inputs for now
            } else {
              rawProps[input.name] = input.value;
            }
          }
        });
      }
      
      // Filter and ensure _delegate_index is always included
      const properties = Object.fromEntries(
        Object.entries(rawProps)
          .filter(([k, v]) => allowedKeys.includes(k) && v !== undefined && v !== null && v !== '')
          .concat([['_delegate_index', d.delegate]])
      );
      
      console.debug('Saving delegate properties:', { line: d.line, delegate: d.delegate, properties });
      
      const lineKey = row.dataset.rowKey; // Use data-row-key as the line item key
      if (!lineKey) {
        console.warn('Missing lineKey for delegate row', { line: d.line, delegate: d.delegate });
        continue;
      }

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 1, properties: { ...properties } })
      });
    }

      // --- Update physical items ---
        const physicalItems = Array.from(this.querySelectorAll('cart-item-row[data-physical="true"]'));
        for (const cartItem of physicalItems) {
          const removeButton = cartItem.querySelector('.cart-item__remove-button');
          const variantId = removeButton ? removeButton.getAttribute('variant-id') : cartItem.getAttribute('variant-id');
          const qtyEl = cartItem.querySelector('.quantity__input');
          let quantity = 1;
          if (qtyEl) {
            quantity = parseInt(qtyEl.value, 10) || 1;
          }
          // Try to get the line key if available
          const lineKey = cartItem.dataset.lineKey;
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: lineKey || variantId,
              quantity: quantity
            })
          });
        }

      /* save attributes */
      await this.saveAttributes(action);
      this.process(false);
      clearAllCache();
    }

    async saveAttributes (action) {
      const fd = new FormData();
      fd.append('attributes[Booking confirmation - Booker & Delegate]', this.cartAttributes.booking);
      fd.append('attributes[Results - Booker & Delegate]', this.cartAttributes.results);
      const res = await fetch('/cart/update.js', { method: 'POST', body: fd }).then(r => r.json());

      if (res.message) {
        const dlg = document.getElementById('modal-errors');
        const body = dlg?.querySelector('.modal-body');
        if (body) body.textContent = res.message;
        dlg?.classList.add('is-open');
      }

      if (action === 'reload') window.location.reload();
      if (action === 'redirect') window.location.href = '/checkout';
    }

    /* ================= UI helpers ================= */
    process (state) {
      this.processing = state;
      const btn = this.form?.querySelector('[type="submit"]');
      if (btn) {
        btn.disabled = state;
        btn.classList.toggle('btn--loading', state);
      }
    }
  });
}
